/**
 * Stock Movement Service — สมุดบัญชีสต็อก
 *
 * Phase 1 = อ่านอย่างเดียว (ledger เขียนโดย backend `services/stock_service.py` เท่านั้น)
 * Phase 3 = เพิ่ม 2 ปาก write ที่คนกดเองได้: รับเข้าคลัง (RESTOCK) และปรับปรุงยอด (ADJUSTMENT)
 *
 * สิทธิ์ (ตรงกับ backend `endpoints/stock_movements.py`):
 * - อ่าน/สรุป/ประวัติรายเครื่อง → `stock_movements.view`
 * - export → `stock_movements.view` **และ** (`stock_movements.export` หรือ `exports.view`)
 * - adjust / receive → `stock_movements.adjust`
 */
import ApiWithLogging from '../utils/apiWithLogging';

// ========== Enum (mirror backend models/stock_movement.py) ==========

export type StockDirection = 'IN' | 'OUT' | 'ADJUST' | 'STATUS_CHANGE';

export type StockReason =
  | 'RENTAL_RESERVE'
  | 'RENTAL_RELEASE'
  | 'RENTAL_COMPLETE'
  | 'RENTAL_RESTORE'
  | 'RESTOCK'
  | 'ADJUSTMENT'
  | 'ISSUE_OUT'
  | 'ISSUE_RETURN'
  | 'WRITE_OFF'
  | 'ACCESSORY_REQUEST'
  | 'EQUIPMENT_RETURN'
  | 'REPLACEMENT'
  | 'MAINTENANCE_IN'
  | 'MAINTENANCE_OUT'
  | 'SUPPLIER_REPAIR'
  | 'IMPORT'
  | 'SIM_EXPIRE'
  | 'PREPARATION_HOLD'
  | 'PREPARATION_RELEASE';

/**
 * label ไทยของ direction/reason/ref_type — **สำเนาสำรอง** ใช้เฉพาะเมื่อ `GET /stock-movements/options`
 * ยังไม่ตอบ/ล้ม (server เก่า, 403) · หน้าจอต้องโหลดตัวเลือกจาก endpoint นั้นก่อนเสมอ
 * (`useStockMovementOptions`) — R2U-04 (8 ก.ย. 2026): สำเนาเดิม 17 ค่าไม่มี PREPARATION_HOLD/RELEASE
 * ทั้งที่ ledger มีแถวจริง เพราะ enum ถูก append ทีหลังตามกติกาแล้วไม่มีใครมาแก้สำเนา
 * ข้อความที่แสดงในตารางให้ใช้ `direction_label` / `reason_label` / `ref_type_label` ที่ backend
 * ส่งมากับแถวเสมอ (แหล่งความจริงอยู่ที่ `schemas/stock_movement.py`)
 */
export const STOCK_DIRECTIONS: ReadonlyArray<{ value: StockDirection; label: string }> = [
  { value: 'IN', label: 'เข้าคลัง' },
  { value: 'OUT', label: 'ออกจากคลัง' },
  { value: 'ADJUST', label: 'ปรับปรุงยอด' },
  { value: 'STATUS_CHANGE', label: 'เปลี่ยนสถานะ' },
];

export const STOCK_REASONS: ReadonlyArray<{ value: StockReason; label: string }> = [
  { value: 'RENTAL_RESERVE', label: 'จองให้สัญญาเช่า' },
  { value: 'RENTAL_RELEASE', label: 'ปล่อยของกลับคลัง (ยกเลิก/ลบสัญญา)' },
  { value: 'RENTAL_COMPLETE', label: 'ปิดการเช่า — รับของกลับ' },
  { value: 'RENTAL_RESTORE', label: 'กู้คืนสัญญา — จองของใหม่' },
  { value: 'RESTOCK', label: 'รับของเข้าคลัง' },
  { value: 'ADJUSTMENT', label: 'ปรับปรุงยอดด้วยมือ' },
  { value: 'ISSUE_OUT', label: 'เบิก/ยืมออกจากคลัง' },
  { value: 'ISSUE_RETURN', label: 'คืนของที่เบิก/ยืม' },
  { value: 'WRITE_OFF', label: 'ตัดจำหน่าย / ชำรุด / สูญหาย' },
  { value: 'ACCESSORY_REQUEST', label: 'ขออุปกรณ์เสริม' },
  { value: 'EQUIPMENT_RETURN', label: 'รับคืนผ่านใบตามเก็บ' },
  { value: 'REPLACEMENT', label: 'เปลี่ยนอุปกรณ์ระหว่างเช่า' },
  // MAINTENANCE_IN / MAINTENANCE_OUT **จงใจไม่อยู่ในสำเนา** — backend ยังไม่มีผู้เขียน (UNWIRED_REASONS
  // ใน schemas/stock_movement.py) เลือกแล้วได้ 0 แถวตลอดกาล · ถ้าวันหนึ่งมีแถวจริง endpoint /options
  // จะเสิร์ฟให้เอง ไม่ต้องแก้ที่นี่
  { value: 'SUPPLIER_REPAIR', label: 'ส่งซ่อมภายนอก / รับกลับ' },
  { value: 'IMPORT', label: 'นำเข้าจาก Excel' },
  { value: 'SIM_EXPIRE', label: 'ซิมหมดอายุ' },
  { value: 'PREPARATION_HOLD', label: 'จองให้ใบงานเตรียมเครื่อง' },
  { value: 'PREPARATION_RELEASE', label: 'ปล่อยการจอง (ยกเลิกใบงาน)' },
];

/** ประเภทอ้างอิง (ref_type) — สำเนาสำรองจาก REF_TYPE_LABELS ฝั่ง backend */
export const STOCK_REF_TYPES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'rental', label: 'สัญญาเช่า' },
  { value: 'rental_item', label: 'รายการในสัญญาเช่า' },
  { value: 'stock_issue', label: 'ใบเบิกอุปกรณ์' },
  { value: 'preparation_job', label: 'ใบงานเตรียมเครื่อง' },
  { value: 'accessory_request', label: 'คำขออุปกรณ์เสริม' },
  { value: 'equipment_return_item', label: 'ใบตามเก็บคืน' },
  { value: 'device_replacement', label: 'การเปลี่ยนอุปกรณ์' },
  { value: 'device_maintenance', label: 'เคสซ่อมบำรุง' },
  { value: 'supplier_repair', label: 'ส่งซ่อมภายนอก' },
  { value: 'device', label: 'อุปกรณ์' },
  { value: 'sim', label: 'ซิม' },
  { value: 'import', label: 'นำเข้า Excel' },
];

// ========== Options (GET /stock-movements/options) ==========

export interface StockOptionItem {
  value: string;
  label: string;
}

/** response ของ `GET /stock-movements/options` — แหล่งความจริงของตัวเลือกตัวกรอง (R2U-04) */
export interface StockMovementOptionsResponse {
  directions: StockOptionItem[];
  reasons: StockOptionItem[];
  ref_types: StockOptionItem[];
}

export interface ResolvedStockOptions {
  directions: ReadonlyArray<StockOptionItem>;
  reasons: ReadonlyArray<StockOptionItem>;
  ref_types: ReadonlyArray<StockOptionItem>;
  /** 'api' = จาก endpoint · 'fallback' = สำเนาในไฟล์นี้ (endpoint ล้ม/ยังไม่ตอบ/ตอบว่าง) */
  source: 'api' | 'fallback';
}

const isOptionList = (v: unknown): v is StockOptionItem[] =>
  Array.isArray(v) &&
  v.length > 0 &&
  v.every((o) => o && typeof o.value === 'string' && typeof o.label === 'string');

/**
 * เลือกใช้ค่าจาก API ก่อน ตกไปสำเนาสำรองเมื่อไม่มี/ล้ม — pure เพื่อเทสต์ได้โดยไม่ต้อง render
 * แต่ละชุด (directions/reasons/ref_types) ตัดสินแยกกัน: API ตอบ reasons ครบแต่ ref_types ว่าง
 * → reasons ใช้ของ API, ref_types ใช้สำเนา
 */
export const resolveStockMovementOptions = (
  data: StockMovementOptionsResponse | null | undefined,
  error?: unknown
): ResolvedStockOptions => {
  const ok = !error && !!data;
  const directions = ok && isOptionList(data!.directions) ? data!.directions : null;
  const reasons = ok && isOptionList(data!.reasons) ? data!.reasons : null;
  const refTypes = ok && isOptionList(data!.ref_types) ? data!.ref_types : null;
  return {
    directions: directions ?? STOCK_DIRECTIONS,
    reasons: reasons ?? STOCK_REASONS,
    ref_types: refTypes ?? STOCK_REF_TYPES,
    source: directions && reasons && refTypes ? 'api' : 'fallback',
  };
};

// ========== Rows ==========

export interface StockMovementRow {
  id: number;
  created_at: string | null;

  direction: StockDirection | string;
  direction_label: string;
  reason_code: StockReason | string;
  reason_label: string;

  quantity: number;
  balance_before: number | null;
  balance_after: number | null;
  status_before: string | null;
  status_after: string | null;
  /** ป้ายไทยจาก backend (BUG-S2313-001) — ห้ามโชว์ค่าดิบ · undefined = backend รุ่นเก่า */
  status_before_label?: string | null;
  status_after_label?: string | null;

  device_id: number | null;
  device_code: string | null;
  device_serial: string | null;
  /**
   * "ประเภท ยี่ห้อ รุ่น" ของอุปกรณ์ (backend `device_kind_name`) เช่น "กระดาษ THERMAL 58mm."
   * — รหัสอย่างเดียว ("DEV178") บอกไม่ได้ว่าเป็นของอะไร · null = ไม่มีข้อมูล/แถวของซิม
   */
  device_name: string | null;
  device_is_serialized: boolean | null;

  sim_id: number | null;
  sim_phone: string | null;

  ref_type: string | null;
  ref_type_label: string | null;
  ref_id: number | null;

  note: string | null;
  performed_by_user_id: number | null;
  /** null = ระบบทำเอง (cron/migration) ไม่ใช่คน */
  performed_by_name: string | null;
}

export interface StockMovementListResponse {
  items: StockMovementRow[];
  total: number;
  page: number;
  page_size: number;
}

// ========== Summary ==========

export interface StockSummaryReasonRow {
  reason_code: string;
  reason_label: string;
  movement_count: number;
  qty_in: number;
  qty_out: number;
  qty_net: number;
  status_change_count: number;
}

export interface StockSummaryPeriodRow {
  period: string;
  movement_count: number;
  qty_in: number;
  qty_out: number;
  qty_net: number;
}

export interface StockSummaryResponse {
  date_from: string | null;
  date_to: string | null;
  total_movements: number;
  total_qty_in: number;
  total_qty_out: number;
  total_qty_net: number;
  by_reason: StockSummaryReasonRow[];
  by_period: StockSummaryPeriodRow[];
}

// ========== Filters ==========

export interface StockMovementFilters {
  page?: number;
  page_size?: number;
  device_id?: number;
  sim_id?: number;
  direction?: StockDirection | '';
  reason_code?: StockReason | '';
  ref_type?: string;
  ref_id?: number;
  date_from?: string;
  date_to?: string;
  search?: string;
}

/** filter ที่ใช้ร่วมกันระหว่าง list / export / summary — ประกอบที่เดียวกันเสมอ
 *  (bug class export-filter-parity: ไฟล์ที่ได้ต้องตรงกับที่เห็นบนจอ) */
const buildFilterParams = (
  filters: Omit<StockMovementFilters, 'page' | 'page_size'>
): Record<string, string | number> => {
  const params: Record<string, string | number> = {};
  if (filters.device_id) params.device_id = filters.device_id;
  if (filters.sim_id) params.sim_id = filters.sim_id;
  if (filters.direction) params.direction = filters.direction;
  if (filters.reason_code) params.reason_code = filters.reason_code;
  if (filters.ref_type) params.ref_type = filters.ref_type;
  if (filters.ref_id) params.ref_id = filters.ref_id;
  if (filters.date_from) params.date_from = filters.date_from;
  if (filters.date_to) params.date_to = filters.date_to;
  if (filters.search) params.search = filters.search;
  return params;
};

// ========== Phase 3: write ==========

export interface StockAdjustRequest {
  device_id: number;
  new_quantity: number;
  reason: string;
}

export interface StockReceiveRequest {
  device_id: number;
  quantity: number;
  supplier_id?: number | null;
  purchase_price?: number | null;
  note?: string | null;
}

/**
 * response ของ adjust/receive = backend `StockBalanceChangeResponse`
 * — คืนยอดก่อน→หลังมาให้ หน้าจอจึงยืนยันผลได้ทันทีโดยไม่ต้องยิงอ่านซ้ำ
 */
export interface StockWriteResponse {
  device_id: number;
  device_code: string | null;
  unit: string | null;
  /** ของมี S/N (รับเข้าคลังได้ตั้งแต่ 9 ก.ย. 2026) — ไม่มียอดคงเหลือ */
  is_serialized: boolean;
  /** null เมื่อเป็นของมี S/N — 1 แถว = 1 เครื่อง ไม่มียอดคงเหลือ (null ≠ 0) */
  balance_before: number | null;
  balance_after: number | null;
  /** balance_after - balance_before (ติดลบได้เมื่อปรับยอดลง) · null เมื่อเป็นของมี S/N */
  delta: number | null;
  minimum_quantity: number;
  /** ยอดใหม่ต่ำกว่าขั้นต่ำ — หน้าจอเตือนเป็นสีส้ม */
  low_stock: boolean;
}

// ========== Service ==========

const stockMovementService = {
  /** ความเคลื่อนไหวทั้งระบบ (ใหม่ → เก่า) */
  async getMovements(filters: StockMovementFilters = {}): Promise<StockMovementListResponse> {
    const params = {
      page: filters.page || 1,
      page_size: filters.page_size || 20,
      ...buildFilterParams(filters),
    };
    return ApiWithLogging.get<StockMovementListResponse>('/stock-movements/', { params });
  },

  /** ตัวเลือกตัวกรอง (ทิศทาง/สาเหตุ/ประเภทอ้างอิง) พร้อมป้ายไทย — R2U-04 · perm stock_movements.view */
  async getOptions(): Promise<StockMovementOptionsResponse> {
    return ApiWithLogging.get<StockMovementOptionsResponse>('/stock-movements/options');
  },

  /** สรุปตามสาเหตุ (+ ตามช่วงเวลาเมื่อส่ง group_by) */
  async getSummary(
    filters: Omit<StockMovementFilters, 'page' | 'page_size' | 'ref_id'> = {},
    groupBy?: 'day' | 'week' | 'month'
  ): Promise<StockSummaryResponse> {
    const params: Record<string, string | number> = { ...buildFilterParams(filters) };
    if (groupBy) params.group_by = groupBy;
    return ApiWithLogging.get<StockSummaryResponse>('/stock-movements/summary', { params });
  },

  /** ประวัติของอุปกรณ์ชิ้นเดียว (เปิดดูได้แม้อุปกรณ์ถูก soft-delete) */
  async getDeviceMovements(
    deviceId: number,
    filters: Omit<StockMovementFilters, 'device_id' | 'sim_id' | 'ref_id'> = {}
  ): Promise<StockMovementListResponse> {
    const params = {
      page: filters.page || 1,
      page_size: filters.page_size || 20,
      ...buildFilterParams({ ...filters, device_id: undefined }),
    };
    return ApiWithLogging.get<StockMovementListResponse>(
      `/devices/${deviceId}/stock-movements`,
      { params }
    );
  },

  /** Export — **ส่ง filter ชุดเดียวกับหน้าจอทุกตัว** */
  async exportToExcel(
    filters: Omit<StockMovementFilters, 'page' | 'page_size'> = {}
  ): Promise<Blob> {
    return ApiWithLogging.get<Blob>(
      '/stock-movements/export/excel',
      { params: buildFilterParams(filters), responseType: 'blob' },
      {
        service: 'stockMovementService',
        action: 'export_stock_movements_excel',
        entityType: 'stock_movement',
      }
    );
  },

  /** Phase 3: ปรับปรุงยอดคงเหลือ (ตรวจนับ / แก้ยอดผิด) — บังคับกรอกเหตุผล */
  async adjust(data: StockAdjustRequest): Promise<StockWriteResponse> {
    return ApiWithLogging.post<StockWriteResponse>(
      '/stock-movements/adjust',
      data,
      {},
      {
        service: 'stockMovementService',
        action: 'adjust_stock',
        entityType: 'stock_movement',
      }
    );
  },

  /** Phase 3: รับของเข้าคลัง (สั่งของเข้า) */
  async receive(data: StockReceiveRequest): Promise<StockWriteResponse> {
    return ApiWithLogging.post<StockWriteResponse>(
      '/stock-movements/receive',
      data,
      {},
      {
        service: 'stockMovementService',
        action: 'receive_stock',
        entityType: 'stock_movement',
      }
    );
  },
};

export default stockMovementService;
